/* ── 204 Part Three §9 — THE CALL REMEMBERS THE MICROPHONE AND THE CAMERA ──────
 *
 * THE DEFECT: CallWindow's device choice was React state born empty at every
 * mount (`devSel`, CallWindow.tsx), `pickDevice` wrote nothing durable, and
 * every acquisition — the ring-time pre-warm, the start, the accept — went
 * through `acquireLocalMedia()` with `audio: true`, the OS default. So a chosen
 * microphone lasted exactly one call window, and the next call took whatever
 * the OS called the default — on a Mac that can be a Teams virtual device that
 * captures silence while the self-view looks perfectly normal.
 *
 * [RULED — advisor's judgement, Laurent overrules at the glance] These are
 * MACHINE-SPECIFIC choices — a deviceId from one Mac means nothing on another
 * — so they live in BROWSER storage, per user, never in the server-side
 * preferences that carry theme and text size across machines. The idiom is
 * App.tsx's `getRecentCases` / `addRecentCase`: one `lx:<name>:<userId>` key
 * in localStorage, JSON, every access in a try/catch (a private window can
 * refuse storage; the call must not care).
 *
 * TWO THINGS THAT DECIDE WHETHER THIS HOLDS (the brief's own):
 *   1  THE LABEL IS STORED BESIDE THE ID. Browsers rotate deviceIds when media
 *      permissions are reset, and a stored id then matches nothing; on restore
 *      the id is matched first, the label second, and only then the default.
 *   2  A STORED DEVICE THAT IS ABSENT RIGHT NOW FALLS BACK TO THE DEFAULT
 *      WITHOUT CLEARING WHAT IS STORED — the camera may simply be unplugged,
 *      and it comes back to the same choice when it is plugged in again.
 *      `resolveStoredDevice` reads and never writes.
 *
 * NO SPEAKER / OUTPUT PICKER: Safari cannot choose an audio output from a web
 * page, and a control that works in one browser and not the other is worse
 * than none.
 */
/* global localStorage, navigator */

export interface StoredDevice { id: string; label: string }
export interface StoredCallDevices { audio: StoredDevice | null; video: StoredDevice | null }
export type DeviceKind = "audio" | "video";
/** The fields of a `MediaDeviceInfo` this module reads. */
export interface DeviceRow { deviceId: string; label: string; kind?: string }

const EMPTY: StoredCallDevices = { audio: null, video: null };
const keyOf = (userId: unknown) => `lx:callDevices:${String(userId)}`;

/** What this browser remembers for this user — `EMPTY` when nothing, or when storage refuses. */
export function readCallDevices(userId: unknown): StoredCallDevices {
  try {
    const raw = localStorage.getItem(keyOf(userId));
    if (!raw) return EMPTY;
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return EMPTY;
    const p = parsed as Partial<Record<DeviceKind, unknown>>;
    const one = (v: unknown): StoredDevice | null => {
      if (!v || typeof v !== "object") return null;
      const d = v as Partial<StoredDevice>;
      return typeof d.id === "string" && d.id !== "" ? { id: d.id, label: typeof d.label === "string" ? d.label : "" } : null;
    };
    return { audio: one(p.audio), video: one(p.video) };
  } catch {
    return EMPTY;
  }
}

/** Remember one kind's choice: an empty id is the DEFAULT and clears that kind alone. */
export function rememberCallDevice(userId: unknown, kind: DeviceKind, id: string, label: string): void {
  if (userId == null || userId === "") return;
  try {
    const prev = readCallDevices(userId);
    const next: StoredCallDevices = { ...prev, [kind]: id ? { id, label } : null };
    localStorage.setItem(keyOf(userId), JSON.stringify(next));
  } catch {
    /* storage refused — the choice still applies to this call */
  }
}

/** The id to ask for, against the devices present NOW: the stored id if it is still
 *  there, else the device carrying the stored label (ids rotate, labels do not), else
 *  null — the default. Reads only: an absent device leaves the store as it is. */
export function resolveStoredDevice(stored: StoredDevice | null, present: readonly DeviceRow[]): string | null {
  if (!stored) return null;
  if (present.some((d) => d.deviceId === stored.id)) return stored.id;
  if (stored.label) {
    const byLabel = present.find((d) => d.label && d.label === stored.label);
    if (byLabel) return byLabel.deviceId;
  }
  return null;
}

/** Both kinds resolved against `enumerateDevices()` — the ids `acquireLocalMedia` asks
 *  for. Nothing stored, or no enumeration (an old browser, a refusal): both null. */
export async function resolveCallDeviceIds(userId: unknown): Promise<{ audioDeviceId: string | null; videoDeviceId: string | null }> {
  const stored = readCallDevices(userId);
  if (!stored.audio && !stored.video) return { audioDeviceId: null, videoDeviceId: null };
  try {
    const all = await navigator.mediaDevices.enumerateDevices();
    const list: DeviceRow[] = all.map((d) => ({ deviceId: d.deviceId, label: d.label, kind: d.kind })).filter((d) => d.deviceId);
    return {
      audioDeviceId: resolveStoredDevice(stored.audio, list.filter((d) => d.kind === "audioinput")),
      videoDeviceId: resolveStoredDevice(stored.video, list.filter((d) => d.kind === "videoinput")),
    };
  } catch {
    return { audioDeviceId: null, videoDeviceId: null };
  }
}
